import React from "react";
import { useTranslation } from "react-i18next";

import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import type { StoreArea } from "@/hooks/useStoreAreas";

interface AreaFilterProps {
  areas: StoreArea[];
  // An empty selection means all areas.
  selectedAreaIds: number[];
  onAreaIdsChange: (areaIds: number[]) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

const AreaFilter: React.FC<AreaFilterProps> = ({
  areas,
  selectedAreaIds,
  onAreaIdsChange,
  disabled = false,
  loading = false,
  className,
}) => {
  const { t } = useTranslation();

  const selectedAreas = React.useMemo(
    () =>
      selectedAreaIds.length === 0
        ? areas
        : areas.filter((area) => selectedAreaIds.includes(area.id)),
    [areas, selectedAreaIds],
  );

  return (
    <MuiAutocompleteMultiSelect
      multiSelect
      options={areas}
      value={selectedAreas}
      onChange={(next) =>
        // Selecting every area, or clearing the selection, filters nothing.
        onAreaIdsChange(next.length === areas.length ? [] : next.map((area) => area.id))
      }
      getOptionLabel={(area) => area.name}
      getOptionKey={(area) => area.id}
      label={t("filters.area")}
      header={t("filters.area")}
      disabled={disabled || loading}
      loading={loading}
      className={["w-full font-bold", className].filter(Boolean).join(" ")}
      minDropdownWidth={360}
      sx={{
        width: { xs: "100%", md: 260 },
        // Fit the search input without resizing when the dropdown opens.
        "& .MuiTextField-root .MuiInputBase-root": { height: 56, minHeight: 56 },
      }}
    />
  );
};

export default AreaFilter;
